import { html, unsafeCSS } from 'lit';
import { customElement, property } from 'lit/decorators.js';
import type { Recipe } from '../chat/types.js';
import { getFieldValue, dispatchChatMarianaLinks } from '../../utils/chat-utils.js';
import styles from '../../styles/mariana-recipe-cards.css?inline';
import { BaseCardCarousel } from './base-card-carousel.js';

/**
 * Recipe Cards Component with Horizontal Scroll
 * Displays a list of recipes in horizontally scrollable cards with scroll indicators
 */
@customElement('mariana-recipe-cards')
export class MarianaRecipeCards extends BaseCardCarousel<Recipe> {
  static readonly styles = unsafeCSS(styles);

  private get isActionLocked(): boolean {
    return this.hasAttribute('data-action-pending') || this.hasAttribute('data-action-locked');
  }

  @property({ type: String })
  source = '';

  private get isAppCC(): boolean {
    return this.source.toLowerCase() === 'appcc';
  }

  @property({ type: Array })
  get recipes(): Recipe[] {
    return this.cards;
  }

  set recipes(value: Recipe[]) {
    this.cards = value;
  }

  protected getContainerClass(): string {
    return 'recipes-scroll-container';
  }

  protected getCardClass(): string {
    return 'recipe-card';
  }

  private handleAddToCart(recipe: Recipe) {
    if (this.isActionLocked) return;

    const recipeId = getFieldValue(recipe, 'id') || recipe.id || '';
    const buttonValue = recipe.button_value || getFieldValue(recipe, 'button_value') || '';
    
    this.dispatchEvent(new CustomEvent('recipe-action', {
      detail: {
        label: '',
        value: buttonValue,
        selectedValues: [String(recipeId)]
      },
      bubbles: true,
      composed: true,
    }));
  }

  private handleImageClick(recipe: Recipe) {
    if (this.isActionLocked) return;

    const pageUrl = recipe.page_url || getFieldValue(recipe, 'page_url');
    if (!pageUrl) return;

    if (this.isAppCC) {
      dispatchChatMarianaLinks(pageUrl);
      return;
    }

    window.open(pageUrl, '_blank');
  }

  protected renderCard(recipe: Recipe, index: number) {
    return html`
      <div class="recipe-card" data-index="${index}">
        <div class="recipe-image-container">
          <img
            src="${getFieldValue(recipe, 'image') || ''}"
            alt="${getFieldValue(recipe, 'name') || 'Recipe'}"
            class="recipe-image"
            @click=${() => this.handleImageClick(recipe)}
            style="cursor: pointer;"
          />
        </div>
        
        <div class="recipe-content">
          <div class="recipe-info">
            <div class="recipe-header">
              <p class="recipe-name">
                ${getFieldValue(recipe, 'name') || 'Sem nome'}
              </p>
              <button
                class="add-to-cart-btn"
                @click=${() => this.handleAddToCart(recipe)}
                title="Adicionar ao carrinho"
                type="button"
              >
                <div class="cart-icon"></div>
              </button>
            </div>
            
            <p class="recipe-description">
              ${getFieldValue(recipe, 'type') || 'Sem categoria'}
            </p>
          </div>
          
          <div class="recipe-meta">
            <div class="meta-item">
              <div class="meta-icon meta-duration-icon"></div>
              <span class="meta-data">${getFieldValue(recipe, 'duration') || '-'}</span>
            </div>
            <div class="meta-item">
              <div class="meta-icon meta-difficulty-icon"></div>
              <span class="meta-data">${getFieldValue(recipe, 'difficulty') || '-'}</span>
            </div>
            <div class="meta-item">
              <div class="meta-icon meta-portion-icon"></div>
              <span class="meta-data">${getFieldValue(recipe, 'portion') || '-'}</span>
            </div>
          </div>
        </div>
      </div>
    `;
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'mariana-recipe-cards': MarianaRecipeCards;
  }
}
